import { CONTENT_HEIGHT_PX } from '@/constants/a4';
import { uboTheme, isLightPageStyle, resolvePageStyleConfig, themeColors, type PageStyle } from './uboTheme';

/**
 * Generates CSS rules for a content section (headings, paragraphs, lists, etc.).
 * Used by both the Tiptap editor (PageSectionView) and the public read-only
 * renderer (ContentPageBlock) to guarantee visual parity.
 *
 * @param selector  CSS class selector to scope rules (e.g. `.section-abc123`)
 * @param style     Page style key from uboTheme
 * @param textColor Resolved text color for the section
 * @param accentColor Merkkleur voor tussenkoppen en lijstnummers (paginastijl Juridisch)
 */
export function buildSectionCss(
  selector: string,
  style: PageStyle,
  textColor: string,
  accentColor: string = uboTheme.colors.primary
): string {
  const { typography, fonts, colors } = uboTheme;
  const bodySize = typography.body.fontSize;
  const bodyLineHeight = typography.body.lineHeight;
  const strokeColor = colors.strokeOnBrand;
  const isDark = !isLightPageStyle(style);

  return `
    ${selector} h1,
    ${selector} h2,
    ${selector} h3 {
      color: ${textColor};
      font-family: ${fonts.primaryFallback};
      text-transform: ${typography.h1.textTransform};
      font-weight: ${typography.h1.fontWeight};
    }
    ${selector} h1 {
      font-size: ${typography.h1.fontSize};
      margin-bottom: ${typography.h1.marginBottom};
      line-height: 1.1;
      ${isDark ? `-webkit-text-stroke-width: 2px; -webkit-text-stroke-color: ${strokeColor}; color: transparent;` : ''}
    }
    ${selector} h2 { font-size: ${typography.h2.fontSize}; margin-bottom: 16px; line-height: 1.2; }
    ${selector} h3 { font-size: ${typography.h3.fontSize}; margin-bottom: 12px; line-height: 1.3; }
    ${selector} p {
      font-family: ${fonts.secondaryFallback};
      font-size: ${bodySize};
      line-height: ${bodyLineHeight};
      font-weight: ${typography.body.fontWeight};
      margin-bottom: 12px;
    }
    ${selector} p:has(> img:not(.ProseMirror-separator):only-child),
    ${selector} figure:has(> img:not(.ProseMirror-separator):only-child) {
      position: static !important;
      float: none !important;
      clear: both;
      display: block;
      max-width: 100%;
      margin: 12px 0;
    }
    ${selector} ul {
      font-family: ${fonts.secondaryFallback};
      font-size: ${bodySize};
      line-height: ${bodyLineHeight};
      font-weight: ${typography.body.fontWeight};
      padding-left: 1.5em;
      margin-bottom: 12px;
      list-style-type: disc;
    }
    ${selector} ol {
      font-family: ${fonts.secondaryFallback};
      font-size: ${bodySize};
      line-height: ${bodyLineHeight};
      font-weight: ${typography.body.fontWeight};
      padding-left: 1.5em;
      margin-bottom: 12px;
      list-style-type: decimal;
    }
    ${selector} li {
      margin-bottom: 4px;
      display: list-item;
    }
    ${selector} ol[type="a"] { list-style-type: lower-alpha; }
    ${selector} ol[type="i"] { list-style-type: lower-roman; }
    /* Lidnummering 2.1, 2.2 (zie listNumberPrefix). */
    ${selector} ol[data-number-prefix] { padding-left: 2.6em; }
    ${selector} ol[data-number-prefix] > li::marker {
      content: var(--number-prefix) counter(list-item) "  ";
    }
    ${isDark ? `${selector} a { color: ${textColor}; text-decoration: underline; }` : ''}
    ${selector} blockquote {
      border-left: 3px solid ${textColor}40;
      padding-left: 1em;
      margin-left: 0;
      font-style: italic;
    }
    /*
     * ProseMirror's inline cursor separator must not clear the Pages floats.
     * Logo's en stijlafbeeldingen van de pagina zelf hebben een vaste maat.
     */
    ${selector} img:not(.ProseMirror-separator):not(.page-chrome-image) {
      position: static !important;
      float: none !important;
      clear: both;
      max-width: 100%;
      max-height: var(--quote-content-page-body-height, ${CONTENT_HEIGHT_PX}px);
      height: auto !important;
      display: block !important;
      object-fit: contain;
      border-radius: 4px;
      margin: 8px 0;
    }
    ${style === 'juridisch' ? buildLegalCss(selector, accentColor) : ''}
  `;
}

/**
 * Paginastijl Juridisch: lange contracttekst compact en rustig, met kleine tussenkoppen en
 * nummers in de merkkleur. Komt na de basisregels en overschrijft alleen maat en ritme.
 */
function buildLegalCss(selector: string, accentColor: string): string {
  return `
    ${selector} h1 { font-size: 32px; margin-bottom: 16px; }
    ${selector} h2 { font-size: 24px; margin-bottom: 12px; }
    ${selector} h3 {
      font-size: 14px;
      letter-spacing: 0.06em;
      line-height: 1.4;
      color: ${accentColor};
      margin: 20px 0 6px;
    }
    ${selector} p,
    ${selector} ul,
    ${selector} ol {
      font-size: 13.5px;
      line-height: 1.55;
    }
    ${selector} p { margin-bottom: 8px; }
    ${selector} ul,
    ${selector} ol {
      padding-left: 2.2em;
      margin-bottom: 8px;
    }
    ${selector} li { margin-bottom: 3px; }
    ${selector} li > p { margin-bottom: 0; }
    ${selector} li::marker {
      color: ${accentColor};
      font-weight: 700;
    }
  `;
}

/**
 * Builds editor-specific CSS additions (caret color, selection highlight).
 * Only needed inside the Tiptap editor, not on the public page.
 */
export function buildEditorSectionCss(
  selector: string,
  style: PageStyle,
  textColor: string,
  accentColor?: string
): string {
  const isDark = !isLightPageStyle(style);
  const caretColor = isDark ? '#ffffff' : 'rgb(0, 7, 236)';
  const selectionBg = isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 7, 236, 0.12)';

  return `
    ${selector} [data-node-view-content] {
      color: ${textColor};
      outline: none;
      caret-color: ${caretColor};
    }
    ${selector} [data-node-view-content] ::selection {
      background: ${selectionBg};
    }
    ${buildSectionCss(selector, style, textColor, accentColor)}
    ${selector} [data-node-view-content] img:not(.ProseMirror-separator) {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 8px 0;
    }
  `;
}

/**
 * Convenience: resolve textColor from a PageStyle and return the CSS.
 */
export function buildSectionCssForStyle(
  selector: string,
  style: PageStyle,
  brand: 'ubo' | 'ubx' = 'ubo'
): string {
  const config = resolvePageStyleConfig(style, brand);
  return buildSectionCss(selector, style, config.textColor, themeColors(brand).primary);
}
